import { ConversationalInterfaceEngine } from './ConversationalEngine';
import { NaturalLanguageProcessor } from './NLPProcessor';
import { chatDemoCopy } from '@/lib/i18n/chat-demo-copy';
import { detectDemoIntent, resolveDemoIntent } from './DemoIntentService';
import { apiClient } from '@/lib/api-client';

export interface ChatIntent {
  primaryAgent: 'tell-tale' | 'zephyr' | 'financial' | 'multi-agent';
  confidence: number;
  context: {
    domain: 'email' | 'support' | 'financial' | 'operations';
    urgency: 'low' | 'medium' | 'high';
    requiresAction: boolean;
    suggestedAgents: string[];
  };
  entities: Array<{
    type: string;
    value: string;
    confidence: number;
  }>;
}

export interface UnifiedChatResponse {
  conversationalText: string;
  primaryAgent: string;
  supportingAgents: string[];
  widget?: {
    type: string;
    data: any;
    actions: string[];
  };
  suggestedActions: Array<{
    actionType: string;
    label: string;
    priority: number;
    agent?: string;
  }>;
  contextFlow?: {
    canTransition: boolean;
    suggestedTransitions: Array<{
      agent: string;
      reason: string;
      prompt: string;
    }>;
  };
  confidence: number;
  agentResponses: Record<string, any>;
}

export class UnifiedChatRouter {
  private conversationalEngine: ConversationalInterfaceEngine;
  private nlpProcessor: NaturalLanguageProcessor;
  private intentPatterns: Map<string, RegExp[]>;

  constructor() {
    this.conversationalEngine = new ConversationalInterfaceEngine();
    this.nlpProcessor = new NaturalLanguageProcessor();
    this.initializeIntentPatterns();
  }

  private initializeIntentPatterns() {
    this.intentPatterns = new Map();

    // BUG-87 (regression): every list below carries its SPANISH equivalents.
    // They were English-only, so a Spanish query matched nothing at all, every
    // agent tied at zero, and the tie-break decided the routing instead of the
    // words. The Spanish demo is a first-class surface — classification has to
    // work on merit in both languages, not fall through to a default.

    // Tell Tale (Email/Document) patterns
    this.intentPatterns.set('tell-tale', [
      /email/i, /message/i, /invoice.*email/i, /document/i, /attachment/i,
      /received.*from/i, /process.*email/i, /extract.*data/i, /classify/i,
      /inbox/i, /correspondence/i, /communication/i, /letter/i, /memo/i,
      /correo/i, /mensaje/i, /documento/i, /adjunto/i, /bandeja/i,
      /clasific/i, /procesar.*correo/i, /comunicaci[oó]n/i, /carta/i
    ]);

    // Zephyr (Support/Help) patterns
    this.intentPatterns.set('zephyr', [
      /help/i, /how.*do/i, /what.*is/i, /explain/i, /guide/i, /tutorial/i,
      /support/i, /issue/i, /problem/i, /error/i, /trouble/i, /broken/i,
      /question/i, /assistance/i, /clarify/i, /understand/i,
      /ayuda/i, /ay[uú]dame/i, /c[oó]mo.*(hago|puedo)/i, /explica/i, /gu[ií]a/i,
      /soporte/i, /problema/i, /duda/i, /pregunta/i, /entender/i, /aclarar/i
    ]);

    // Financial (Operations) patterns
    this.intentPatterns.set('financial', [
      /cash/i, /payment/i, /invoice/i, /bill/i, /vendor/i, /supplier/i,
      /receivable/i, /payable/i, /contract/i, /qualify/i, /credit/i,
      /balance/i, /forecast/i, /accelerate/i, /optimize/i, /cross.*border/i,
      /efectivo/i, /liquidez/i, /pago/i, /factura/i, /proveedor/i, /cliente/i,
      /cobrar/i, /pagar/i, /contrato/i, /cr[eé]dito/i, /saldo/i, /flujo de caja/i,
      /pron[oó]stico/i, /acelerar/i, /optimizar/i, /capital de trabajo/i
    ]);

    // Multi-agent (Complex) patterns
    this.intentPatterns.set('multi-agent', [
      /email.*cash/i, /payment.*problem/i, /invoice.*delayed.*impact/i,
      /late.*payment.*what.*do/i, /email.*says.*optimize/i,
      /process.*and.*analyze/i, /handle.*and.*show/i
    ]);
  }

  async processUnifiedQuery(query: string, context: any): Promise<UnifiedChatResponse> {
    try {
      // BUG-87 (regression): resolve the Phase-1 financial intents BEFORE the
      // demo-mode gate, not after it.
      //
      // `detectDemoIntent` is bilingual and `resolveDemoIntent` derives every
      // figure from `fetchDemoFinancialData()` — the same tenant data the
      // dashboard cards read — degrading to an honest "no data" / "couldn't
      // reach it" message rather than a fabricated number. None of that is
      // demo-only, so none of it belongs behind `demo_mode`.
      //
      // Gating it is what sent "¿Cuál es mi posición de efectivo?" to Tell
      // Tale: `demo_mode` lives in per-tab sessionStorage, so any session that
      // reaches the dashboard without passing through DemoAccountSetup reads
      // `false`, fell through to `classifyIntent`, and — because those patterns
      // are English-only — tied every score at 0 and lost the tie-break to the
      // first entry in the map. Intent now governs, the flag no longer does.
      const seededIntent = detectDemoIntent(query);
      if (seededIntent !== 'out_of_scope') {
        console.log('[UnifiedChatRouter] Phase-1 intent matched:', seededIntent);
        return await this.handleSeededDataIntent(query);
      }

      // Check if we're in demo mode
      const isDemoMode = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('demo_mode') === 'true';
      console.log('[UnifiedChatRouter] processUnifiedQuery - isDemoMode:', isDemoMode);
      console.log('[UnifiedChatRouter] sessionStorage demo_mode value:', sessionStorage?.getItem('demo_mode'));

      if (isDemoMode) {
        console.log('[UnifiedChatRouter] Routing to handleDemoQuery');
        return await this.handleDemoQuery(query, context);
      }

      // 1. Classify the intent
      const intent = await this.classifyIntent(query);

      // 2. Route to appropriate handler
      switch (intent.primaryAgent) {
        case 'tell-tale':
          return await this.handleEmailQuery(query, context, intent);
        case 'zephyr':
          return await this.handleSupportQuery(query, context, intent);
        case 'financial':
          return await this.handleFinancialQuery(query, context, intent);
        case 'multi-agent':
          // BUG-104: this used to answer "I've coordinated multiple agents to
          // handle your complex request", naming Tell Tale, Windrose and
          // Poniente at 0.85 confidence, while `agentResponses` came back empty
          // — nothing ran. Multi-agent coordination is not built, so the
          // assistant no longer claims it. Anything reaching here already
          // failed the Phase-1 intent match above, so the honest answer is the
          // capability message naming what we CAN do.
          return await this.handleSeededDataIntent(query);
        default:
          return await this.handleUnknownQuery(query, context);
      }
    } catch (error) {
      console.error('Unified chat processing error:', error);
      return this.generateErrorResponse(error.message);
    }
  }

  private async classifyIntent(query: string): Promise<ChatIntent> {
    const normalizedQuery = query.toLowerCase();
    const scores = new Map<string, number>();

    // Score each agent type
    for (const [agent, patterns] of this.intentPatterns) {
      let score = 0;
      for (const pattern of patterns) {
        if (pattern.test(normalizedQuery)) {
          score += 1;
        }
      }
      scores.set(agent, score);
    }

    // Find the highest scoring agent.
    //
    // BUG-87 (regression): when NOTHING matched, `maxScore` is 0 and
    // `.find(score === maxScore)` happily returns the FIRST entry in the map —
    // 'tell-tale' — so "no idea what this is" silently rendered as "this is
    // definitely about email". The `|| 'financial'` default below could never
    // fire, because `.find` always found something. A zero score is an absence
    // of evidence, not a vote: fall back to the declared default explicitly.
    const maxScore = Math.max(...scores.values());
    const primaryAgent =
      maxScore === 0
        ? 'financial'
        : Array.from(scores.entries()).find(([_, score]) => score === maxScore)?.[0] || 'financial';

    // Determine domain and context
    const domain = this.getDomain(primaryAgent);
    const urgency = this.detectUrgency(query);
    const requiresAction = this.detectActionRequired(query);

    return {
      primaryAgent: primaryAgent as any,
      confidence: maxScore > 0 ? Math.min(maxScore / 3, 1) : 0.5,
      context: {
        domain,
        urgency,
        requiresAction,
        suggestedAgents: this.getSuggestedAgents(primaryAgent, query)
      },
      entities: await this.nlpProcessor.parse(query).then(p => p.entities)
    };
  }

  private async handleEmailQuery(query: string, context: any, intent: ChatIntent): Promise<UnifiedChatResponse> {
    // Integration with existing Tell Tale functionality
    const emailContext = await this.getEmailContext();
    
    const copy = chatDemoCopy();

    return {
      conversationalText: `${copy.emailIntro} ${this.generateEmailResponse(query, emailContext)}`,
      primaryAgent: 'Tell Tale',
      supportingAgents: intent.context.suggestedAgents.filter(a => a !== 'Tell Tale'),
      // BUG-114: `data` used to be `emailContext.recentEmails` — a bare ARRAY —
      // while EmailListWidget destructures `data.emails`. `data.emails` was
      // therefore always undefined, the widget fell back to [] and rendered
      // "0 total" regardless of what the array held. The widget's totalCount
      // and the sentence's count now come from the same source, so the two can
      // no longer contradict each other in the same frame.
      widget: emailContext.hasEmails ? {
        type: 'email-list',
        data: {
          emails: emailContext.recentEmails,
          totalCount: emailContext.emailCount,
          unreadCount: emailContext.recentEmails.filter(e => e.status === 'unread').length,
        },
        actions: ['process', 'categorize', 'extract']
      } : undefined,
      // BUG-114: these were hardcoded English literals rendered inside a
      // Spanish session.
      suggestedActions: [
        { actionType: 'process-emails', label: copy.emailActionProcess, priority: 1, agent: 'Tell Tale' },
        { actionType: 'view-inbox', label: copy.emailActionViewInbox, priority: 2, agent: 'Tell Tale' }
      ],
      contextFlow: this.generateContextFlow(intent, 'email'),
      confidence: intent.confidence,
      agentResponses: { 'Tell Tale': { processed: true, emailCount: emailContext.emailCount } }
    };
  }

  private async handleSupportQuery(query: string, context: any, intent: ChatIntent): Promise<UnifiedChatResponse> {
    // Integration with existing Zephyr functionality
    const supportResponse = await this.getZephyrResponse(query);
    
    return {
      conversationalText: supportResponse.text,
      primaryAgent: 'Zephyr',
      supportingAgents: intent.context.suggestedAgents.filter(a => a !== 'Zephyr'),
      widget: supportResponse.hasGuide ? {
        type: 'help-guide',
        data: supportResponse.guide,
        actions: ['follow-guide', 'get-more-help']
      } : undefined,
      suggestedActions: [
        { actionType: 'help', label: 'Get More Help', priority: 1, agent: 'Zephyr' },
        { actionType: 'tutorial', label: 'View Tutorial', priority: 2, agent: 'Zephyr' }
      ],
      contextFlow: this.generateContextFlow(intent, 'support'),
      confidence: intent.confidence,
      agentResponses: { 'Zephyr': supportResponse }
    };
  }

  private async handleFinancialQuery(query: string, context: any, intent: ChatIntent): Promise<UnifiedChatResponse> {
    // Use existing conversational engine
    const financialResponse = await this.conversationalEngine.processConversation({
      userQuery: query,
      context,
      sessionId: context.sessionId || 'unified',
      timestamp: new Date()
    });

    const agentsThatRan = Object.keys(financialResponse.agentResponses);

    return {
      conversationalText: financialResponse.conversationalText,
      // BUG-108: only attribute the answer to a financial specialist when one
      // actually produced it. `detectPrimaryFinancialAgent` reads keywords off
      // the QUERY, so it happily credited Levante with a credit answer that no
      // agent computed — the BUG-105 shape, one layer up. Since deleting the
      // mock orchestrator, nothing runs on this path, so it attributes to
      // Zephyr (support), matching what `handleSeededDataIntent` returns for an
      // out-of-scope question.
      primaryAgent: agentsThatRan.length > 0 ? this.detectPrimaryFinancialAgent(query) : 'Zephyr',
      supportingAgents: agentsThatRan,
      widget: financialResponse.widget,
      suggestedActions: financialResponse.suggestedActions,
      contextFlow: this.generateContextFlow(intent, 'financial'),
      confidence: financialResponse.confidence,
      agentResponses: financialResponse.agentResponses
    };
  }


  private generateContextFlow(intent: ChatIntent, currentDomain: string) {
    const transitions = [];

    // Suggest transitions based on current domain and detected entities
    if (currentDomain === 'email' && intent.entities.some(e => e.type === 'amount')) {
      transitions.push({
        agent: 'Financial AI',
        reason: 'Detected financial amounts in email',
        prompt: 'Analyze the financial impact of this email'
      });
    }

    if (currentDomain === 'financial' && intent.context.urgency === 'high') {
      transitions.push({
        agent: 'Zephyr Support',
        reason: 'High urgency financial issue',
        prompt: 'Get help resolving this urgent financial matter'
      });
    }

    return {
      canTransition: transitions.length > 0,
      suggestedTransitions: transitions
    };
  }

  /**
   * The tenant's real recent emails (BUG-114).
   *
   * This used to be a stub returning `{ hasEmails: true, emailCount: 5, ... }`
   * under a "Mock implementations for integration points" comment, so the card
   * always said "Encontré 5 correos recientes por procesar" no matter what the
   * tenant had. demo-mx has zero rows in `email_messages`; the widget beneath
   * the sentence said "0 total" in the same frame.
   *
   * `/emails` has existed since the payables/receivables restoration work and
   * is RLS-scoped to the session's company — it was simply never wired up.
   * Because `hasEmails` was hardcoded true, the `emailNone` branch below (which
   * already existed, already translated) was unreachable.
   *
   * A failed request reports NO emails rather than a guess: an unreachable
   * inbox is not evidence of five messages.
   */
  private async getEmailContext(): Promise<{
    hasEmails: boolean;
    emailCount: number;
    recentEmails: Array<{
      id: string;
      from: string;
      subject: string;
      priority: 'high' | 'medium' | 'low';
      status: 'unread';
      received_at?: string;
    }>;
  }> {
    const empty = { hasEmails: false, emailCount: 0, recentEmails: [] as any[] };
    try {
      const res = await apiClient.get<{ data: any[] | null; error: string | null }>('/emails', {
        limit: '20',
      });
      if (res?.error) return empty;
      const rows = Array.isArray(res?.data) ? res.data : [];
      const recentEmails = rows.map((row: any) => ({
        id: String(row.id),
        from: row.from_email || 'unknown',
        subject: row.subject || '',
        priority: 'medium' as const,
        status: 'unread' as const,
        received_at: row.received_at || row.created_at || undefined,
      }));
      return {
        hasEmails: recentEmails.length > 0,
        emailCount: recentEmails.length,
        recentEmails,
      };
    } catch {
      return empty;
    }
  }

  private async getZephyrResponse(query: string) {
    return {
      text: `I can help you with that! Based on your question about "${query}", here's what I recommend...`,
      hasGuide: true,
      guide: { steps: ['Step 1', 'Step 2'], estimated_time: '5 minutes' }
    };
  }

  private detectPrimaryFinancialAgent(query: string): string {
    if (query.includes('cash')) return 'Windrose';
    if (query.includes('payment') || query.includes('accelerate')) return 'Poniente';
    if (query.includes('risk') || query.includes('credit')) return 'Levante';
    if (query.includes('contract')) return 'Levante';
    if (query.includes('cross-border')) return 'Hooked In';
    return 'Financial AI';
  }


  // NOTE (BUG-104): `handleMultiAgentQuery` and `coordinateMultipleAgents` lived
  // here. They returned a summary asserting that Tell Tale, Windrose and
  // Poniente had coordinated on the user's request — with hardcoded
  // `activeAgents`, a hardcoded `confidence: 0.85`, and an EMPTY
  // `agentResponses`. No agent ran. That is a false claim about the product's
  // own behaviour, not merely a wrong number, and Alisio's differentiator is
  // those agents. Multi-agent coordination is not built, so the claim is gone
  // rather than back-filled with plausible-looking values. The 'multi-agent'
  // switch arm now falls through to the seeded capability message.

  private getDomain(agent: string): 'email' | 'support' | 'financial' | 'operations' {
    switch (agent) {
      case 'tell-tale': return 'email';
      case 'zephyr': return 'support';
      case 'financial': return 'financial';
      default: return 'operations';
    }
  }

  private detectUrgency(query: string): 'low' | 'medium' | 'high' {
    if (/urgent|emergency|asap|immediately|critical/.test(query.toLowerCase())) return 'high';
    if (/soon|important|priority/.test(query.toLowerCase())) return 'medium';
    return 'low';
  }

  private detectActionRequired(query: string): boolean {
    return /should|can|need|want|how|what.*do/.test(query.toLowerCase());
  }

  private getSuggestedAgents(primaryAgent: string, query: string): string[] {
    const suggestions = [];
    
    if (primaryAgent !== 'tell-tale' && /email|document/.test(query)) {
      suggestions.push('Tell Tale');
    }
    if (primaryAgent !== 'zephyr' && /help|how/.test(query)) {
      suggestions.push('Zephyr');
    }
    if (primaryAgent !== 'financial' && /cash|payment/.test(query)) {
      suggestions.push('Windrose', 'Poniente');
    }
    
    return suggestions;
  }

  private generateEmailResponse(query: string, emailContext: any): string {
    const copy = chatDemoCopy();
    if (emailContext.hasEmails) {
      return copy.emailFound(emailContext.emailCount);
    }
    return copy.emailNone;
  }

  private async handleUnknownQuery(query: string, context: any): Promise<UnifiedChatResponse> {
    return {
      conversationalText: chatDemoCopy().unknownQuery(query),
      primaryAgent: 'System',
      supportingAgents: [],
      suggestedActions: [
        { actionType: 'help', label: 'Get Help', priority: 1 },
        { actionType: 'examples', label: 'Show Examples', priority: 2 }
      ],
      confidence: 0.1,
      agentResponses: {}
    };
  }

  private generateErrorResponse(error: string): UnifiedChatResponse {
    return {
      conversationalText: chatDemoCopy().errorResponse,
      primaryAgent: 'System',
      supportingAgents: [],
      suggestedActions: [
        { actionType: 'retry', label: 'Try Again', priority: 1 },
        { actionType: 'support', label: 'Contact Support', priority: 2 }
      ],
      confidence: 0,
      agentResponses: {}
    };
  }

  /**
   * AI-Native Demo Mode Handler
   * Provides context-aware, data-driven responses that showcase true AI capabilities
   */
  private async handleDemoQuery(query: string, context: any): Promise<UnifiedChatResponse> {
    console.log('[UnifiedChatRouter] handleDemoQuery called with:', query);
    const queryLower = query.toLowerCase();
    const currentPage = context?.currentPage || 'dashboard';

    // PRD-38 Phase 1 (cash position, payables, receivables, overdue) is no
    // longer dispatched here: BUG-87 hoisted it into `processUnifiedQuery`
    // ABOVE the demo-mode gate, because those answers are derived from the
    // tenant's own data and are correct with or without the flag. Anything
    // reaching this method is already `out_of_scope` for Phase 1, so re-running
    // `detectDemoIntent` would be dead code — and dead code is precisely how
    // the original cash-flow depth went missing for seven weeks.

    // 1. Detect query type WITHOUT the page-context override. PRD-38 lets the
    //    user's WORDS govern: a weather question asked while standing on the
    //    Receivables page must NOT inherit a receivables answer (that produced a
    //    garbled fabricated figure before this fix). We only honor a canned
    //    handler when the query genuinely matched a keyword.
    const keywordContext = this.analyzeQueryContext(query, '__no_page__');
    console.log('[UnifiedChatRouter] Keyword-only context:', keywordContext);

    // 2. Route only the non-Phase-1 capabilities that the canned handlers still
    //    own (compliance, risk, forecast/analysis, help). The Phase-1 topics
    //    (cash-flow / receivables / payables) are owned by DemoIntentService and
    //    were already handled above when their keywords matched, so a bare
    //    page-context match for those must NOT re-enter the canned path.
    if (keywordContext.type === 'financial-analysis') {
      // BUG-104: forecast/trend queries used to get a fabricated "analysis"
      // built from MOCK_DEMO_DATA_BASE constants, asserted as the tenant's own
      // data. We cannot forecast, so we say what we can do instead.
      return await this.handleSeededDataIntent(query);
    } else if (keywordContext.type === 'risk-analysis' || keywordContext.type === 'compliance') {
      // BUG-105: these returned static templates opening "Levante Credit Risk
      // Engine analyzing..." / "Hooked In Compliance Monitor analyzing...",
      // naming two more of the eight agents as actively working. Neither ran.
      // Worse than BUG-104's empty claim: they populated `agentResponses` with
      // corroborating detail (`customersAnalyzed: 47`, `complianceChecksRun:
      // 150`) that was equally invented, plus hardcoded widget figures
      // (portfolioScore 6.2, complianceRate 0.98, savingsYTD 42300).
      // Credit scoring and compliance checking do not run on these queries, so
      // the assistant no longer says they do.
      return await this.handleSeededDataIntent(query);
    } else if (keywordContext.type === 'general-help' && this.isExplicitHelpQuery(queryLower)) {
      // Only the genuine "help me understand the app" ask reaches the help
      // widget. A bare "what …" (e.g. "what's the weather") is NOT a help
      // request — it is out of scope and gets the capability message below.
      return this.generateHelpResponse(query, keywordContext, currentPage);
    }

    // 3. PRD-38 AC7: anything else is genuinely out of scope — return the
    //    graceful capability message (which lists the Phase-1 intents) instead
    //    of a canned answer that could read as a fabricated number.
    return await this.handleSeededDataIntent(query);
  }

  /** True only for explicit "help me with the app" asks (EN + ES). */
  private isExplicitHelpQuery(queryLower: string): boolean {
    return /\b(help|ayuda|tutorial|guide|gu[ií]a|how do i|how can i|c[oó]mo puedo)\b/.test(
      queryLower
    );
  }

  /**
   * PRD-38 Phase 1 — seeded-data-aware intent handler (hybrid mechanism).
   * Delegates to DemoIntentService which computes figures deterministically
   * from the seeded dataset and phrases them via the existing Gemini provider
   * (phrasing-only, with deterministic fallback). The numbers it returns match
   * the dashboard / Receivables / Payables surfaces to the dollar.
   */
  private async handleSeededDataIntent(query: string): Promise<UnifiedChatResponse> {
    const result = await resolveDemoIntent(query);
    const copy = chatDemoCopy();

    // BUG-87: a cash-position answer also carries the cash-flow chart and the
    // follow-up actions it had before PRD-38. Both are driven by the SAME
    // deterministic figures as the text, so the widget can never disagree with
    // the words. Only attached when figures were actually computed — a
    // timed-out or empty-state answer must not render a chart of nothing.
    const hasFigures = Object.keys(result.figures).length > 0;
    const isCashFlow = result.intent === 'cash_position' && hasFigures;
    // BUG-101: receivables and payables carry their widget + actions too. They
    // were cash-flow-only, which is half of why those two answers read as a
    // dead end — no chart, no next step, two sentences. Same rule as cash flow:
    // only attached when figures were actually computed, so a timed-out or
    // empty answer never renders a chart of nothing.
    const isReceivables = result.intent === 'receivables' && hasFigures;
    const isPayables = result.intent === 'payables' && hasFigures;

    const supportingAgents = isCashFlow
      ? ['Windrose', 'Levante']
      : isReceivables
        ? ['Poniente', 'Levante']
        : isPayables
          ? ['Windrose']
          : [];

    let widget: UnifiedChatResponse['widget'];
    if (isCashFlow) {
      widget = {
        type: 'cash-flow-chart',
        data: {
          workingCapital: result.figures.cashPosition,
          receivablesTotal: result.figures.totalReceivables,
          payablesTotal: result.figures.totalPayables,
          netPosition: result.figures.netPosition,
          overdueAmount: result.figures.overdue,
        },
        actions: ['apply-optimization', 'view-scenario', 'schedule-payments'],
      };
    } else if (isReceivables) {
      widget = {
        type: 'receivables-dashboard',
        data: {
          total: result.figures.receivablesTotal,
          current: result.figures.current,
          agingThirty: result.figures.agingThirty,
          agingSixty: result.figures.agingSixty,
          agingNinety: result.figures.agingNinety,
          agingNinetyPlus: result.figures.agingNinetyPlus,
          overdueAmount: result.figures.overdue,
        },
        actions: ['send-reminders', 'create-payment-plans', 'escalate'],
      };
    } else if (isPayables) {
      widget = {
        type: 'payables-scheduler',
        data: {
          total: result.figures.payablesTotal,
          dueThisWeek: result.figures.dueThisWeek,
          dueSoon: result.figures.dueSoon,
        },
        actions: ['auto-schedule', 'review-terms', 'negotiate'],
      };
    }

    let suggestedActions: UnifiedChatResponse['suggestedActions'] = [];
    if (isCashFlow) {
      suggestedActions = [
        { actionType: 'optimize-payments', label: copy.cashFlowActions[0], priority: 1, agent: 'Poniente' },
        { actionType: 'early-payment', label: copy.cashFlowActions[1], priority: 2, agent: 'Poniente' },
        { actionType: 'forecast-scenario', label: copy.cashFlowActions[2], priority: 3, agent: 'Windrose' },
      ];
    } else if (isReceivables) {
      suggestedActions = [
        { actionType: 'view-receivables', label: copy.receivablesActions[0], priority: 1, agent: 'Windrose' },
        { actionType: 'payment-plans', label: copy.receivablesActions[1], priority: 2, agent: 'Poniente' },
        { actionType: 'risk-review', label: copy.receivablesActions[2], priority: 3, agent: 'Levante' },
      ];
    } else if (isPayables) {
      suggestedActions = [
        { actionType: 'auto-schedule', label: copy.payablesActions[0], priority: 1, agent: 'Poniente' },
        { actionType: 'review-discounts', label: copy.payablesActions[1], priority: 2, agent: 'Poniente' },
        { actionType: 'negotiate-terms', label: copy.payablesActions[2], priority: 3, agent: 'Full Send' },
      ];
    }

    return {
      conversationalText: result.text,
      primaryAgent: result.primaryAgent,
      supportingAgents,
      widget,
      suggestedActions,
      confidence: 0.95,
      agentResponses: {
        [result.primaryAgent]: {
          intent: result.intent,
          figures: result.figures,
          phrased: result.phrased,
          mechanism: 'hybrid',
        },
      },
    };
  }

  private analyzeQueryContext(query: string, currentPage: string): any {
    const queryLower = query.toLowerCase();

    // Context-aware analysis based on current page and query content.
    // Keywords cover English AND Spanish so the Spanish demo routes to the
    // same specialized responses instead of the generic fallback (BUG-29).
    const matches = (...words: string[]) => words.some((w) => queryLower.includes(w));
    if (matches('cash', 'liquidity', 'runway', 'efectivo', 'liquidez', 'flujo de caja')) {
      return { type: 'cash-flow', urgency: 'medium', context: currentPage };
    } else if (matches('risk', 'credit', 'default', 'riesgo', 'crédito', 'credito', 'incumplimiento')) {
      return { type: 'risk-analysis', urgency: 'high', context: currentPage };
    } else if (matches('overdue', 'collect', 'vencida', 'vencidas', 'cobrar', 'cobranza') || (currentPage === 'receivables')) {
      return { type: 'receivables', urgency: 'medium', context: currentPage };
    } else if (matches('payable', 'vendor', 'por pagar', 'proveedor') || (currentPage === 'payables')) {
      return { type: 'payables', urgency: 'low', context: currentPage };
    } else if (matches('compliance', 'usmca', 'regulation', 'cumplimiento', 't-mec', 'regulación', 'regulacion')) {
      return { type: 'compliance', urgency: 'high', context: currentPage };
    } else if (matches('help', 'how', 'what', 'ayuda', 'cómo', 'como', 'qué', 'que')) {
      return { type: 'general-help', urgency: 'low', context: currentPage };
    } else if (matches('forecast', 'predict', 'trend', 'pronóstico', 'pronostico', 'predicción', 'prediccion', 'tendencia')) {
      return { type: 'financial-analysis', urgency: 'medium', context: currentPage };
    }

    return { type: 'general', urgency: 'low', context: currentPage };
  }


  // NOTE (BUG-104): `generateFinancialInsight` lived here. It opened with
  // "Based on your demo account data, I've identified key financial trends"
  // and then quoted $487,234 as the cash position — which is
  // MOCK_DEMO_DATA_BASE.cashPosition.currentBalance, not the tenant's. For the
  // MX tenant the real figure is $26.3M. It also shipped a hardcoded
  // `dso: 42`, `cashTrend: [450, 465, 478, 487]`, `workingCapital: 2.3`,
  // `cashOptimizationPotential: 89000` and `topCustomerConcentration: 0.47`.
  //
  // Forecasting and trend analysis are not built. The fix is NOT to swap the
  // constants for plausible derived ones — a forecast we cannot compute must
  // not be presented at all. Forecast/trend queries now get the capability
  // message naming what the assistant genuinely can answer.

  // NOTE (BUG-87): `generateCashFlowInsight` lived here. PRD-38 stopped calling
  // it (0 call sites) which is how the cash-flow depth silently disappeared.
  // Its content now lives in the Phase-1 seeded path — DemoIntentService's
  // `buildCashPosition` (text, same `chatDemoCopy().cashFlow` template) plus
  // `handleSeededDataIntent` (widget + actions) — so there is ONE cash-flow
  // answer, not two divergent copies.


  // NOTE (BUG-105): `generateRiskInsight` lived here. It reported a portfolio
  // score of 6.2, $142,000 of exposure, a 2.3% default probability and claimed
  // Levante had analysed 47 customers — none of it computed from anything.
  // Credit risk analysis is not built; the claim is removed rather than
  // back-filled with plausible values.



  // NOTE (BUG-101): `generateReceivablesInsight` and `generatePayablesInsight`
  // lived here with 0 call sites — PRD-38's `detectDemoIntent` intercepts in
  // `processUnifiedQuery` before either could be reached, the same orphaning
  // that hid the cash-flow depth in BUG-87.
  //
  // Both answers now live in the Phase-1 seeded path: DemoIntentService's
  // `buildReceivables` / `buildPayables` (text, from the tenant's own seeded
  // rows) plus `handleSeededDataIntent` above (widget + actions). ONE answer
  // per intent, not two divergent copies.
  //
  // The receivables content was carried over intact — it was genuinely
  // data-driven. The payables content was NOT: it was a hardcoded block naming
  // invented suppliers ("SupplyCo Industries", "TechVendor LLC", "GlobalParts
  // Inc") with invented figures, which would have contradicted the dashboard on
  // any real tenant. It was rebuilt from the seeded bills instead.
  //
  // `generateMultiAgentDemo` was orphaned the same way and is gone too: no
  // intent routed to it, the live multi-agent path answers elsewhere, and its
  // body was status flags only. An orphaned generator makes the codebase look
  // like it ships a capability it does not, which is how this goes unnoticed.


  // NOTE (BUG-105): `generateComplianceInsight` lived here. It reported a 98%
  // compliance rate, $42,300 saved YTD and claimed Hooked In had run 150
  // compliance checks finding 3 issues. Nothing ran. USMCA compliance checking
  // is a real product surface, but this handler was not wired to it — it was a
  // static string. Removed rather than made to look computed.

  private generateHelpResponse(query: string, queryContext: any, currentPage: string): UnifiedChatResponse {
    const copy = chatDemoCopy();
    const pageHelpText = copy.pageHelp[currentPage] || copy.pageHelp.fallback;

    return {
      conversationalText: copy.help(currentPage, pageHelpText),
      primaryAgent: 'Zephyr',
      supportingAgents: [],
      widget: {
        type: 'help-guide',
        data: {
          currentPage,
          availableAgents: 8,
          features: ['Context-aware AI', 'Multi-agent coordination', 'Actionable insights']
        },
        actions: ['explore-agents', 'view-tutorial', 'contact-support']
      },
      suggestedActions: [
        { actionType: 'explore-dashboard', label: copy.helpActions[0], priority: 1, agent: 'Zephyr' },
        { actionType: 'try-example', label: copy.helpActions[1], priority: 2, agent: 'Zephyr' },
        { actionType: 'view-tutorial', label: copy.helpActions[2], priority: 3, agent: 'Zephyr' }
      ],
      confidence: 1.0,
      agentResponses: {
        'Zephyr': { helpProvided: true }
      }
    };
  }

}